import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface Stakeholder {
  id: string;
  solutionId: string;
  externalKey: string | null;
  name: string;
  role: string;
  org: "client" | "nymbl";
  authority: "low" | "med" | "high";
  initials: string;
  email: string | null;
  createdAt: string;
}

export interface StakeholderCreateBody {
  name: string;
  role: string;
  org: "client" | "nymbl";
  email?: string | null;
}

const stakeholderKeys = {
  list: (solutionId: string) => ["stakeholders", "list", solutionId] as const,
} as const;

async function readError(res: Response): Promise<string> {
  try {
    const json = await res.json();
    return json?.error?.message ?? json?.message ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export function useStakeholders(solutionId: string) {
  return useQuery({
    queryKey: stakeholderKeys.list(solutionId),
    queryFn: async (): Promise<Stakeholder[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/stakeholders`);
      if (!res.ok) throw new Error("Failed to fetch stakeholders");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useCreateStakeholder(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StakeholderCreateBody): Promise<Stakeholder> => {
      const res = await fetch(`/api/solutions/${solutionId}/stakeholders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await readError(res));
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stakeholderKeys.list(solutionId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteStakeholder(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stakeholderId: string) => {
      const res = await fetch(`/api/stakeholders/${stakeholderId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await readError(res));
      return stakeholderId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stakeholderKeys.list(solutionId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
